/* eslint-disable react/prop-types */ // Props are validated via TypeScript interfaces
import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import NumberInput from 'components/form/managed_input/number_input';

interface DemographicsMapProps {
  latitude: number;
  longitude: number;
  radiusColor?: string;
  radiusRings?: (number | null)[];
  showRadiusRings: boolean;
  onRadiusRingChange?: (index: number, value: number | null) => void;
  onAddRadiusRing?: () => void;
  onRemoveRadiusRing?: (index: number) => void;
  maxRadiusCount?: number;
  minRadius?: number;
  maxRadius?: number;
  radiusStep?: number;
  onToggleShowRadiusRings?: (checked: boolean) => void;
}

const DemographicsMap: React.FC<DemographicsMapProps> = ({
  latitude,
  longitude,
  radiusRings = [],
  radiusColor = '#435674',
  showRadiusRings = false,
  onRadiusRingChange,
  onAddRadiusRing,
  onRemoveRadiusRing,
  maxRadiusCount = 3,
  minRadius = 0.1,
  maxRadius = 100,
  radiusStep = 0.05
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const circlesRef = useRef<google.maps.Circle[]>([]);

  // Helper functions
  const milesToMeters = (miles: number) => miles * 1609.34;

  // Extract opacity calculation to a reusable function
  const getOpacityForIndex = (index: number) => Math.max(0.1, 0.35 - (index * 0.08));

  // Validate lat/lng coordinates
  const isValidCoordinate = (lat: number, lng: number): boolean => {
    return !isNaN(lat) && !isNaN(lng) &&
           lat !== null && lat !== undefined &&
           lng !== null && lng !== undefined &&
           lat >= -90 && lat <= 90 &&
           lng >= -180 && lng <= 180;
  };

  if (!isValidCoordinate(latitude, longitude)) {
    return (
      <div className="demographics-map-wrapper" style={{ position: 'relative' }}>
        <div
          className="demographics-map-error"
          style={{
            width: '100%',
            height: '560px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '16px',
            textAlign: 'center'
          }}
        >
          <div>
            <div style={{ marginBottom: '8px', fontSize: '18px' }}>📍</div>
            <div>Invalid coordinates provided</div>
          </div>
        </div>
      </div>
    );
  }

  // Initialize map when component mounts
  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const mapOptions = {
      zoom: 14,
      minZoom: 4,
      streetViewControl: false,
      mapTypeControl: true,
      tilt: 0,
      center: {
        lat: latitude,
        lng: longitude
      },
      gestureHandling: 'cooperative',
      mapId: 'demographics-map'
    };

    // Create map
    const newMap = new google.maps.Map(mapRef.current, mapOptions);
    setMap(newMap);

    // Create marker
    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      map: newMap,
      position: mapOptions.center,
      gmpDraggable: false
    });
    setMarker(newMarker);

    return () => {
      // Cleanup circles on component unmount
      if (circlesRef.current) {
        circlesRef.current.forEach(circle => circle.setMap(null));
        circlesRef.current = [];
      }
    };
  }, []);

  // Update marker position and circles when lat/lng or radiusRings change
  useEffect(() => {
    if (!map || !marker) return;

    const position = {
      lat: latitude,
      lng: longitude
    };

    marker.position = position;
    map.setCenter(position);

    // Clear any existing circles
    if (circlesRef.current) {
      circlesRef.current.forEach(circle => circle.setMap(null));
      circlesRef.current = [];
    }

    // Only draw circles for filled rings; freshly added (null) rings have no
    // value yet and should not render a 0-radius circle.
    const filledRadii = radiusRings.filter((r): r is number => typeof r === 'number' && r > 0);

    if (filledRadii.length > 0) {
      circlesRef.current = filledRadii.map((radiusMiles, index) => {
        const opacity = getOpacityForIndex(index);
        const fillColor = radiusColor || '#3B82F6';

        return new google.maps.Circle({
          map,
          center: position,
          radius: milesToMeters(radiusMiles),
          fillColor: fillColor,
          fillOpacity: opacity,
          strokeColor: radiusColor,
          strokeOpacity: 0.8,
          strokeWeight: 1,
          clickable: false
        });
      });

      // Fix zoom so that we can fit all circles in the viewport on first load
      const bounds = new google.maps.LatLngBounds();
      const largestRadius = Math.max(...filledRadii);

      // Make sure the geometry library is loaded before using these functions
      if (google.maps.geometry && google.maps.geometry.spherical) {
        const northEast = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(position.lat, position.lng),
          milesToMeters(largestRadius) * 1.1,
          45
        );
        const southWest = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(position.lat, position.lng),
          milesToMeters(largestRadius) * 1.1,
          225
        );
        bounds.extend(northEast);
        bounds.extend(southWest);
        map.fitBounds(bounds);
      } else {
        map.setZoom(Math.max(10, 14 - Math.floor(largestRadius / 2)));
      }
    }
  }, [latitude, longitude, radiusRings, showRadiusRings, map, marker]);

  // Legend component
  const renderLegend = () => {
    // Always render the legend shell so user can toggle on/off

    const numberInputStyles: React.CSSProperties = {
      width: 'calc(4em + 30px)', // widen content area so spinners shift right
      marginRight: '10px',
      boxSizing: 'content-box',
      fontSize: '17px',
      borderRadius: '6px'
    };

    return (
      <div
        className="map-legend"
        style={{
          position: 'absolute',
          bottom: '30px',
          right: '10px',
          backgroundColor: 'white',
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          fontSize: '14px',
          zIndex: 1000,
          width: '280px',
          maxWidth: 'calc(100% - 20px)',
          maxHeight: '70%',
          overflowY: 'auto'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ fontWeight: 'bold' }}>Radius Rings</div>
        </div>
        <p> Changes made to the radius rings will be reflected in your documents.</p>
        {radiusRings.map((radius, index) => {
          const opacity = getOpacityForIndex(index);
          const canRemove = !!onRemoveRadiusRing && radiusRings.length > 1;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginBottom: '12px'
              }}
            >
              <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Radius {index + 1}</p>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    backgroundColor: radiusColor || '#3B82F6',
                    opacity: opacity,
                    borderRadius: '50%',
                    marginRight: '8px',
                    border: `1px solid ${radiusColor || '#2563EB'}`,
                    flexShrink: 0
                  }}
                />
                {onRadiusRingChange
                  ? (
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <NumberInput
                        className="demographics-radius-input"
                        inputMode="decimal"
                        maxValue={maxRadius}
                        minValue={minRadius}
                        pattern="[0-9]*[.]?[0-9]+"
                        step={radiusStep}
                        style={numberInputStyles}
                        value={radius}
                        onChange={() => {}}
                        onValueChange={v => onRadiusRingChange(index, v)}
                      />
                      <span>Miles</span>
                      {canRemove && (
                        <button
                          aria-label={`Remove Radius ${index + 1}`}
                          className="btn btn-link p-0 ms-auto"
                          style={{
                            color: '#6b7280',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: 1,
                            marginLeft: 'auto'
                          }}
                          title="Remove this radius"
                          type="button"
                          onClick={() => onRemoveRadiusRing(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span>{radius} miles</span>
                  )}
              </div>
            </div>
          );
        })}
        {onAddRadiusRing && radiusRings.length < maxRadiusCount && (
          <button
            className="btn btn-link p-0"
            style={{ fontWeight: 600, textDecoration: 'none' }}
            type="button"
            onClick={onAddRadiusRing}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
            Add Radius
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="demographics-map-wrapper" style={{ position: 'relative' }}>
      <div
        className="demographics-map"
        ref={mapRef}
        style={{ width: '100%', height: '560px', marginBottom: '15px' }}
      />
      {renderLegend()}
    </div>
  );
};

export default DemographicsMap;